import React, { useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Typography,
  Box,
  Paper,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Launch as LaunchIcon,
  Link as LinkIcon,
} from "@mui/icons-material";
import { format } from "date-fns";
import { Timestamp } from "firebase/firestore";
import {
  fetchOrganizationEventsTruncated,
  fetchOrganizationOrdersTruncated,
  fetchOrganizationSquareLocations,
  fetchOrganizationSquareTokens,
  crmSliceSelector,
} from "../../../redux/features/crmSlice";
import { statusForEvent } from "../../../redux/features/adminEventsSlice";
import EventStatusView from "../../AdminEvents/EventsList/EventListItem/components/EventStatusView";

export default function CRMOrganizationDetails() {
  const { organizationId } = useParams();
  const dispatch = useDispatch();
  const crmState = useSelector((state) => crmSliceSelector(state));
  const {
    organizationDetails,
    organizationEventsTruncatedWithEnhancedTicketTypes,
    organizationEventsTruncatedLoading,
    organizationEventStatsTruncated,
    organizationOrdersTruncated,
    organizationOrdersTruncatedLoading,
    organizationSquareLocations,
    organizationSquareLocationsLoading,
    organizationSquareTokens,
    organizationSquareTokensLoading,
    usersMap,
  } = crmState;

  const organization = organizationDetails[organizationId];

  const eventsMap =
    organizationEventsTruncatedWithEnhancedTicketTypes[organizationId];
  const eventStatsMap = organizationEventStatsTruncated[organizationId] || {};
  const orders = organizationOrdersTruncated[organizationId];
  const squareLocations = organizationSquareLocations[organizationId] || [];
  const squareTokens = organizationSquareTokens[organizationId];

  // Track what we've attempted to fetch to prevent infinite loops
  const fetchAttemptedRef = useRef({
    events: false,
    orders: false,
    squareLocations: false,
    squareTokens: false,
  });

  useEffect(() => {
    if (!organizationId) return;

    // Reset fetch attempts when organizationId changes
    fetchAttemptedRef.current = {
      events: false,
      orders: false,
      squareLocations: false,
      squareTokens: false,
    };
  }, [organizationId]);

  useEffect(() => {
    if (!organizationId) return;

    // Only fetch if not already loading, data doesn't exist, and we haven't attempted yet
    if (
      eventsMap === undefined &&
      !organizationEventsTruncatedLoading[organizationId] &&
      !fetchAttemptedRef.current.events
    ) {
      fetchAttemptedRef.current.events = true;
      dispatch(fetchOrganizationEventsTruncated({ organizationId }));
    }
    if (
      orders === undefined &&
      !organizationOrdersTruncatedLoading[organizationId] &&
      !fetchAttemptedRef.current.orders
    ) {
      fetchAttemptedRef.current.orders = true;
      dispatch(fetchOrganizationOrdersTruncated({ organizationId }));
    }
    if (
      organizationSquareLocations[organizationId] === undefined &&
      !organizationSquareLocationsLoading[organizationId] &&
      !fetchAttemptedRef.current.squareLocations
    ) {
      fetchAttemptedRef.current.squareLocations = true;
      dispatch(fetchOrganizationSquareLocations({ organizationId }));
    }
    if (
      squareTokens === undefined &&
      !organizationSquareTokensLoading[organizationId] &&
      !fetchAttemptedRef.current.squareTokens
    ) {
      fetchAttemptedRef.current.squareTokens = true;
      dispatch(fetchOrganizationSquareTokens({ organizationId }));
    }
  }, [
    organizationId,
    eventsMap,
    organizationEventsTruncatedLoading,
    orders,
    organizationOrdersTruncatedLoading,
    organizationSquareLocations,
    organizationSquareLocationsLoading,
    squareTokens,
    organizationSquareTokensLoading,
    dispatch,
  ]);

  const getUserDisplayName = (userId) => {
    const user = usersMap[userId];
    if (!user) return userId;
    return user.displayName || user.email || userId;
  };

  const eventsList = eventsMap ? Object.values(eventsMap) : [];

  // Sort upcoming events by start date ascending
  const sortedEvents = eventsList.sort((a, b) => {
    const aDate = a.startsAt?.toMillis?.() || 0;
    const bDate = b.startsAt?.toMillis?.() || 0;
    return aDate - bDate;
  });

  const sortedOrders = orders
    ? [...orders].sort((a, b) => {
        const aDate = a.createdAt?.toMillis?.() || 0;
        const bDate = b.createdAt?.toMillis?.() || 0;
        return bDate - aDate;
      })
    : [];

  const sortedSquareTokens = squareTokens
    ? [...squareTokens].sort((a, b) => {
        if (a.status === "ACTIVE" && b.status !== "ACTIVE") return -1;
        if (a.status !== "ACTIVE" && b.status === "ACTIVE") return 1;
        const aDate = a.createdAt?.toMillis?.() || 0;
        const bDate = b.createdAt?.toMillis?.() || 0;
        return bDate - aDate;
      })
    : [];

  // Build users list from organization's owner, admins, and scanners
  const organizationUsers = React.useMemo(() => {
    if (!organization) return [];
    const userIdsSet = new Set();
    if (organization.owner) {
      userIdsSet.add(organization.owner);
    }
    if (organization.admins && Array.isArray(organization.admins)) {
      organization.admins.forEach((id) => userIdsSet.add(id));
    }
    if (organization.scanners && Array.isArray(organization.scanners)) {
      organization.scanners.forEach((id) => userIdsSet.add(id));
    }

    return Array.from(userIdsSet)
      .map((userId) => {
        const user = usersMap[userId];
        if (!user) return null;

        // Determine roles for this organization
        const roles = [];
        if (organization.owner === userId) {
          roles.push("Owner");
        }
        if (
          organization.admins &&
          Array.isArray(organization.admins) &&
          organization.admins.includes(userId)
        ) {
          roles.push("Admin");
        }
        if (
          organization.scanners &&
          Array.isArray(organization.scanners) &&
          organization.scanners.includes(userId)
        ) {
          roles.push("Scanner");
        }

        return {
          ...user,
          roles,
        };
      })
      .filter(Boolean)
      .sort((a, b) => {
        // Sort by role priority (Owner > Admin > Scanner), then by name
        const roleOrder = { Owner: 0, Admin: 1, Scanner: 2 };
        const aRolePriority = Math.min(
          ...a.roles.map((role) => roleOrder[role] ?? 99),
        );
        const bRolePriority = Math.min(
          ...b.roles.map((role) => roleOrder[role] ?? 99),
        );
        if (aRolePriority !== bRolePriority) {
          return aRolePriority - bRolePriority;
        }
        const aName = a.displayName || a.email || "";
        const bName = b.displayName || b.email || "";
        return aName.localeCompare(bName);
      });
  }, [organization, usersMap]);

  return (
    <>
      {/* Organization Info Section */}
      <Box marginTop={4}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom>
            Organization Information
          </Typography>
          <Box marginTop={2}>
            <Typography variant="body2" color="text.secondary">
              <strong>ID:</strong> {organization?.id}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Owner:</strong>{" "}
              {organization?.owner ? (
                <Typography
                  variant="body2"
                  component={Link}
                  to={`/crm/users/${organization.owner}`}
                  sx={{
                    textDecoration: "none",
                    color: "primary.main",
                    display: "inline",
                  }}
                >
                  {getUserDisplayName(organization.owner)}
                </Typography>
              ) : (
                "N/A"
              )}
            </Typography>
            {organization?.createdAt && (
              <Typography variant="body2" color="text.secondary">
                <strong>Created:</strong>{" "}
                {format(organization.createdAt.toDate(), "MMM d, yyyy")}
              </Typography>
            )}
            {organization?.admins && organization.admins.length > 0 && (
              <Typography variant="body2" color="text.secondary">
                <strong>Admins:</strong>{" "}
                {organization.admins.map((id, idx) => (
                  <React.Fragment key={id}>
                    <Typography
                      variant="body2"
                      component={Link}
                      to={`/crm/users/${id}`}
                      sx={{
                        textDecoration: "none",
                        color: "primary.main",
                        display: "inline",
                      }}
                    >
                      {getUserDisplayName(id)}
                    </Typography>
                    {idx < organization.admins.length - 1 && (
                      <Typography variant="body2" component="span">
                        {", "}
                      </Typography>
                    )}
                  </React.Fragment>
                ))}
              </Typography>
            )}
            {organization?.scanners && organization.scanners.length > 0 && (
              <Typography variant="body2" color="text.secondary">
                <strong>Scanners:</strong>{" "}
                {organization.scanners.map((id, idx) => (
                  <React.Fragment key={id}>
                    <Typography
                      variant="body2"
                      component={Link}
                      to={`/crm/users/${id}`}
                      sx={{
                        textDecoration: "none",
                        color: "primary.main",
                        display: "inline",
                      }}
                    >
                      {getUserDisplayName(id)}
                    </Typography>
                    {idx < organization.scanners.length - 1 && (
                      <Typography variant="body2" component="span">
                        {", "}
                      </Typography>
                    )}
                  </React.Fragment>
                ))}
              </Typography>
            )}
          </Box>
        </Paper>
      </Box>

      {/* Events Section */}
      <Box marginTop={4}>
        <Paper sx={{ p: 3 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            marginBottom={2}
          >
            <Typography variant="h6">Upcoming Events</Typography>
          </Box>
          {organizationEventsTruncatedLoading[organizationId] ? (
            <LinearProgress sx={{ marginTop: 2 }} />
          ) : sortedEvents.length === 0 ? (
            <Typography variant="body2" color="text.secondary" marginTop={2}>
              No events found
            </Typography>
          ) : (
            <TableContainer sx={{ marginTop: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Starts</TableCell>
                    <TableCell>Ends</TableCell>
                    <TableCell>Tickets Sold</TableCell>
                    <TableCell>Revenue</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedEvents.slice(0, 10).map((event) => {
                    const startsDate = event.startsAt?.toDate
                      ? format(event.startsAt.toDate(), "MMM d, yyyy h:mm a")
                      : "N/A";
                    const endsDate = event.endsAt?.toDate
                      ? format(event.endsAt.toDate(), "MMM d, yyyy h:mm a")
                      : "N/A";
                    const createdDate = event.createdAt?.toDate
                      ? format(event.createdAt.toDate(), "MMM d, yyyy")
                      : "N/A";
                    // Get event stats
                    const stats = eventStatsMap[event.id];
                    const totalSoldCount = stats?.sold || 0;
                    const totalCollected = stats?.totalUsdCents ?? 0;
                    const refundedUsdCents = stats?.refundedUsdCents ?? 0;
                    const netRevenueUsdCents = totalCollected - refundedUsdCents;
                    const totalSoldUsdCents = event.totalSoldUsdCents;

                    // Format revenue amount (net of refunds)
                    let revenueText = "";
                    if (totalCollected !== undefined || refundedUsdCents > 0) {
                      revenueText = new Intl.NumberFormat("en-US", {
                        style: "currency",
                        currency: "USD",
                      }).format(netRevenueUsdCents / 100);
                    } else if (totalSoldUsdCents !== undefined) {
                      revenueText = new Intl.NumberFormat("en-US", {
                        style: "currency",
                        currency: "USD",
                      }).format(totalSoldUsdCents / 100);
                    }

                    // Merge stats into event for statusForEvent (ticket types already enhanced by selector)
                    const eventWithStats = {
                      ...event,
                      stats,
                    };

                    return (
                      <TableRow key={event.id}>
                        <TableCell>
                          <Typography variant="body2">
                            {event.name || "Unnamed Event"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <EventStatusView
                            status={statusForEvent(eventWithStats).status}
                            squareLocationError={event.squareLocationError}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{startsDate}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{endsDate}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {totalSoldCount}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {revenueText || "N/A"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{createdDate}</Typography>
                        </TableCell>
                        <TableCell>
                          {event.publicUrl ? (
                            <Box display="flex" gap={1}>
                              <Tooltip title="View public event page">
                                <IconButton
                                  component={Link}
                                  to={event.publicUrl}
                                  target="_blank"
                                  size="small"
                                >
                                  <LaunchIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Copy public URL">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    navigator.clipboard.writeText(
                                      event.publicUrl,
                                    );
                                  }}
                                >
                                  <LinkIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              -
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {!organizationEventsTruncatedLoading[organizationId] &&
            sortedEvents.length > 0 && (
              <Box marginTop={2} display="flex" justifyContent="flex-end">
                <Button
                  component={Link}
                  to={`/crm/events?filter_include_organizationId=${organizationId}`}
                  variant="outlined"
                >
                  Show All Events
                </Button>
              </Box>
            )}
        </Paper>
      </Box>

      {/* Orders Section */}
      <Box marginTop={4}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Recent Orders
          </Typography>
          {organizationOrdersTruncatedLoading[organizationId] ? (
            <LinearProgress sx={{ marginTop: 2 }} />
          ) : sortedOrders.length === 0 ? (
            <Typography variant="body2" color="text.secondary" marginTop={2}>
              No orders found
            </Typography>
          ) : (
            <TableContainer sx={{ marginTop: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Order ID</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Tickets</TableCell>
                    <TableCell>Created</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedOrders.slice(0, 10).map((order) => {
                    const createdDate = order.createdAt?.toDate
                      ? format(order.createdAt.toDate(), "MMM d, yyyy h:mm a")
                      : "N/A";
                    const amount =
                      order.totalUsdCents !== undefined
                        ? `$${((order.totalUsdCents || 0) / 100).toFixed(2)}`
                        : "N/A";

                    // Build tickets summary from items
                    const ticketsSummary =
                      (order.items || [])
                        .map(
                          (item) =>
                            `${item.quantity} x ${item.name || "Unknown"}`,
                        )
                        .join(", ") || "N/A";

                    return (
                      <TableRow key={order.id}>
                        <TableCell>
                          <Typography variant="body2">{order.id}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{amount}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {ticketsSummary}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{createdDate}</Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {!organizationOrdersTruncatedLoading[organizationId] &&
            sortedOrders.length > 0 && (
              <Box marginTop={2} display="flex" justifyContent="flex-end">
                <Button
                  component={Link}
                  to={`/crm/orders?filter_include_organizationId=${organizationId}`}
                  variant="outlined"
                >
                  Show All Orders
                </Button>
              </Box>
            )}
        </Paper>
      </Box>

      {/* Users Section */}
      <Box marginTop={4}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Users
          </Typography>
          {organizationUsers.length === 0 ? (
            <Typography variant="body2" color="text.secondary" marginTop={2}>
              No users found
            </Typography>
          ) : (
            <TableContainer sx={{ marginTop: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Roles</TableCell>
                    <TableCell>Last Login</TableCell>
                    <TableCell>Created</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {organizationUsers.map((user) => {
                    const createdDate = user.createdAt?.toDate
                      ? format(user.createdAt.toDate(), "MMM d, yyyy")
                      : "N/A";

                    // Handle lastLogin - could be Timestamp instance or serialized object
                    let lastLoginDate = "Never";
                    if (user.lastLogin) {
                      let lastLoginTimestamp = user.lastLogin;
                      // If it's a serialized timestamp object, convert it
                      if (
                        user.lastLogin.seconds &&
                        user.lastLogin.nanoseconds !== undefined
                      ) {
                        lastLoginTimestamp = new Timestamp(
                          user.lastLogin.seconds,
                          user.lastLogin.nanoseconds,
                        );
                      }
                      // If it has toDate method, use it
                      if (lastLoginTimestamp?.toDate) {
                        lastLoginDate = format(
                          lastLoginTimestamp.toDate(),
                          "MMM d, yyyy h:mm a",
                        );
                      }
                    }

                    return (
                      <TableRow key={user.id}>
                        <TableCell>
                          <Typography
                            variant="body2"
                            fontWeight="medium"
                            component={Link}
                            to={`/crm/users/${user.id}`}
                            sx={{
                              textDecoration: "none",
                              color: "primary.main",
                              "&:hover": {
                                textDecoration: "underline",
                              },
                            }}
                          >
                            {user.displayName || "No name"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {user.email || "N/A"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {user.roles.join(", ")}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {lastLoginDate}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{createdDate}</Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Box>

      {/* Square Integration Section */}
      <Box marginTop={4}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom>
            Square Integration
          </Typography>

          {/* Square Tokens */}
          <Box marginTop={3}>
            <Typography variant="h6" gutterBottom>
              Access Tokens
            </Typography>
            {organizationSquareTokensLoading[organizationId] ? (
              <LinearProgress sx={{ marginTop: 2 }} />
            ) : sortedSquareTokens.length === 0 ? (
              <Typography variant="body2" color="text.secondary" marginTop={2}>
                No Square access tokens found
              </Typography>
            ) : (
              <TableContainer sx={{ marginTop: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Status</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell>Updated</TableCell>
                      <TableCell>Expires</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedSquareTokens.map((token) => {
                      const createdDate = token.createdAt?.toDate
                        ? format(token.createdAt.toDate(), "MMM d, yyyy h:mm a")
                        : "N/A";
                      const updatedDate = token.updatedAt?.toDate
                        ? format(token.updatedAt.toDate(), "MMM d, yyyy h:mm a")
                        : "N/A";
                      const expiresDate = token.expiresAt?.toDate
                        ? format(token.expiresAt.toDate(), "MMM d, yyyy h:mm a")
                        : "N/A";
                      const isActive = token.status === "ACTIVE";

                      return (
                        <TableRow key={token.id}>
                          <TableCell>
                            {isActive ? (
                              <Box display="flex" alignItems="center" gap="4px">
                                <CheckCircleIcon
                                  color="success"
                                  fontSize="small"
                                />
                                <Typography
                                  variant="body2"
                                  color="success.main"
                                >
                                  Active
                                </Typography>
                              </Box>
                            ) : (
                              <Box display="flex" alignItems="center" gap="4px">
                                <ErrorIcon color="error" fontSize="small" />
                                <Typography variant="body2" color="error.main">
                                  {token.status || "UNKNOWN"}
                                </Typography>
                              </Box>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {createdDate}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {updatedDate}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {expiresDate}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>

          {/* Square Locations */}
          <Box marginTop={4}>
            <Typography variant="h6" gutterBottom>
              Square Locations
            </Typography>
            {organizationSquareLocationsLoading[organizationId] ? (
              <LinearProgress sx={{ marginTop: 2 }} />
            ) : squareLocations.length === 0 ? (
              <Typography variant="body2" color="text.secondary" marginTop={2}>
                No Square locations found
              </Typography>
            ) : (
              <TableContainer sx={{ marginTop: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Square Location ID</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Capabilities</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {squareLocations.map((location) => {
                      const isActive = location.status === "ACTIVE";
                      const canProcessCreditCards =
                        location.capabilities &&
                        Array.isArray(location.capabilities) &&
                        location.capabilities.includes(
                          "CREDIT_CARD_PROCESSING",
                        );

                      return (
                        <TableRow key={location.id}>
                          <TableCell>
                            <Typography variant="body2">
                              {location.name || "Unnamed Location"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {location.id || "N/A"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {isActive ? (
                              <Box display="flex" alignItems="center" gap="4px">
                                <CheckCircleIcon
                                  color="success"
                                  fontSize="small"
                                />
                                <Typography
                                  variant="body2"
                                  color="success.main"
                                >
                                  Active
                                </Typography>
                              </Box>
                            ) : (
                              <Box display="flex" alignItems="center" gap="4px">
                                <ErrorIcon color="error" fontSize="small" />
                                <Typography variant="body2" color="error.main">
                                  {location.status || "UNKNOWN"}
                                </Typography>
                              </Box>
                            )}
                          </TableCell>
                          <TableCell>
                            {canProcessCreditCards ? (
                              <Box display="flex" alignItems="center" gap="4px">
                                <CheckCircleIcon
                                  color="success"
                                  fontSize="small"
                                />
                                <Typography
                                  variant="body2"
                                  color="success.main"
                                >
                                  Credit Card Processing
                                </Typography>
                              </Box>
                            ) : (
                              <Box display="flex" alignItems="center" gap="4px">
                                <ErrorIcon color="error" fontSize="small" />
                                <Typography variant="body2" color="error.main">
                                  No Credit Card Processing
                                </Typography>
                              </Box>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </Paper>
      </Box>
    </>
  );
}
